import Item from './item';
import ItemStatus from './item-status';
import Order from './order';

export class CircularDependencyError extends Error {
    constructor(message: string) {
        super(message);
        // https://stackoverflow.com/questions/41102060/typescript-extending-error-class
        Object.setPrototypeOf(this, CircularDependencyError.prototype);
    }
}

export class InvalidDependencyError extends Error {
    constructor(message: string) {
        super(message);
        // https://stackoverflow.com/questions/41102060/typescript-extending-error-class
        Object.setPrototypeOf(this, InvalidDependencyError.prototype);
    }
}

export interface Node<T> {
    id: number | string;
    edges: (number | string)[];
    value: T;
}

export default class OrderUtils {

    /**
   * Private utility that recursively gets all replaced items for a given item,
   * with most recent to least recent in the array
   *
   * @param order {Order} An Order
   * @param item {Item} An item
   * @param replacedItems {Item[]} list of items that gets updated when a replaced item is found.
   * @returns {*}
   */
    private static getReplacedItemsHelper(order: Order, item: Item, replacedItems: Item[]): any {
        return order.items
            .filter((it) => it.type === item.type)
            .reduce((acc, currentItem) => {
                if (currentItem.replacedById === item.id) {
                    acc.push(currentItem);
                    return this.getReplacedItemsHelper(order, currentItem, acc);
                } else {
                    return acc;
                }
            }, replacedItems);
    }

    /**
   * Recursively searches node edges [depth first] for node values which match the given predicate.
   * NOTE: Order Items must be wrapped in Node interface in order to be used with this method.
   *
   * @param {Node<T>} currentNode The node that is currently being inspected
   * @param {Set<Node<T>>} seen Set of nodes which have been visited. Used to short circuit joined dependencies
   * @param {Node<T>[]} path Current path of graph transit; used to detect circular dependencies
   * @param {Map<string | number, Node<T>>} allItems Map of all nodes within the graph
   * @param {(T) => boolean} predicate Filter function determines if node value can be returned
   * @returns {T[]} The values of nodes which match the given predicate, and are in the tree of the current node
   */
    static recursiveSearch<T>(currentNode: Node<T>,
        seen: Set<Node<T>>,
        path: Node<T>[],
        allItems: Map<string | number, Node<T>>,
        predicate: (t: T, node: Node<T>) => boolean): T[] {

        if (path.includes(currentNode)) {
            throw new CircularDependencyError(`Circular dependency detected: ` +
        `${[currentNode].concat(path).reverse().map((x) => x.id).join(' -> ')}`);
        }
        if (seen.has(currentNode)) {
            return [];
        }

        seen.add(currentNode);
        path = [currentNode].concat(path);

        const option: T[] = [currentNode.value].filter((it) => predicate(it, currentNode));

        if (currentNode.edges.length === 0) {
            return option;
        }
        else {
            return option.concat(
                currentNode.edges
                    .map((id) => {
                        if (!allItems.has(id)) {
                            throw new InvalidDependencyError(`Item ${currentNode.id} contains invalid dependency: ${id}`);
                        }
                        return allItems.get(id);
                    })
                    .map((it) => this.recursiveSearch(it, seen, path, allItems, predicate))
                    .reduce((acc, value) => acc.concat(value), []),
            );
        }
    }

    /**
   * Finds all items in the order, which are dependencies of the rootItem and match the given predicate.
   * NOTE: The root item will be included in the search results if it matches the predicate.
   *
   * @param {Order} order The order
   * @param {Item} rootItem The item from which to search
   * @param {(Item) => boolean} nodePredicate Indicates if a given item is a match
   * @param {includeOptionalDependencies: boolean, itemFilter: (Item)=>boolean} additionalOptions includeOptionalDependencies indicates if optional dependencies should be traversed, defaults to false if not provided.
   * @param {(Item) => boolean} edgePredicate determines whether a given item's edges should be traversed
   * @returns {Item[]}
   */
    static findItemDependencies(order: Order, rootItem: Item, nodePredicate: (item: Item) => boolean, additionalOptions: { includeOptionalDependencies: boolean} = { includeOptionalDependencies: false}, edgePredicate: (item: Item) => boolean = () => true): Item[] {
        const map = new Map(order.items.map((it) => {
            let edges = [];
            if (edgePredicate(it)) {
                if (additionalOptions.includeOptionalDependencies) {
                    edges = [...it.dependencies, ...it.optionalDependencies];
                } else {
                    edges = [...it.dependencies];
                }
            }
            return [ it.id, { id: it.id, edges, value: it }] as [string, Node<Item>];
        }),
        );

        return this.recursiveSearch(
            map.get(rootItem.id),
            new Set(),
            [],
            map,
            nodePredicate);
    }

    /**
    * @deprecated Renamed to findDependentItems
    */
    static findUpstreamItems(order: Order, rootItem: Item, predicate: (item: Item) => boolean): Item[] {
        return this.findDependentItems(order, rootItem, predicate);
    }

    /**
   * Finds all items in the order which depend on the root item and match the given predicate.
   * Note: The root item will be included in the search results if it matches the predicate
   *
   * @param {Order} order The order
   * @param {Item} rootItem The item from which to search
   * @param {(Item) => boolean} nodePredicate Indicates if a given item is a match
   * @param {includeOptionalDependencies: boolean, itemFilter: (Item)=>boolean} additionalOptions includeOptionalDependencies indicates if optional dependencies should be traversed, defaults to false if not provided.
   * @param {(Item) => boolean} edgePredicate determines whether a given item's edges should be traversed
   * @returns {Item[]}
   */
    static findDependentItems(order: Order, rootItem: Item, nodePredicate: (item: Item) => boolean, additionalOptions: { includeOptionalDependencies: boolean} = { includeOptionalDependencies: false}, edgePredicate: (item: Item) => boolean = () => true): Item[] {

        // Same as above, but leave edges empty
        const map = new Map(
            order.items.map((it) => {
                return [it.id, {id: it.id, edges: [], value: it}] as [string, Node<Item>];
            }),
        );

        // Add edges in the opposite direction of the dependencies
        order.items.forEach((it) => {
            if (edgePredicate(it)) {
                (additionalOptions.includeOptionalDependencies ? [...it.dependencies, ...it.optionalDependencies] as string[] : [...it.dependencies]).forEach((depId) => {
                    if (!map.has(depId)) {
                        throw new InvalidDependencyError(`Item ${it.id} contains invalid dependency: ${depId}`);
                    }
                    map.get(depId).edges.push(it.id);
                });
            }
        });

        return this.recursiveSearch(
            map.get(rootItem.id),
            new Set(),
            [],
            map,
            nodePredicate);
    }

    /**
   * Finds all items in the given order which matches the given predicate. Dependencies are not inspected
   *
   * @param {Order} order An order
   * @param {(Item, Map<string, Item>) => boolean} predicate Indicates if a given item is a match
   * @returns {Item[]}
   */
    static findItems(order: Order, predicate: (item: Item, map: Map<string, Item>) => boolean): Item[] {
        const map = this.itemMap(order);

        return order.items.filter((item) => predicate(item, map));
    }

    /**
   * Returns a list of items which are in ready (in ToDo status and have no unfulfilled direct dependencies),
   * and match the given itemType if present. If no itemType is provided, ready items of any type will be returned
   *
   * @param {Order} order An order
   * @param {string} itemType An optional filter that restricts the type of items that will be found
   * @returns {Item[]}
   */
    static findReadyItems(order: Order, itemType: string = null): Item[] {
        return this.findItems(order, (item, map) => {
            if (!!itemType && item.type !== itemType) {
                return false;
            }

            return this.itemIsReady(order, item, map);
        });
    }

    static itemMap(order: Order) {
        return new Map(
            order.items.map((it) => {
                return [it.id, it] as [string, Item];
            }),
        );
    }

    /**
     * Determines if the given item is ready
     *
     * @param order {Order} An order
     * @param item {Item} An item
     * @param [map] {Map.<string, Item>} Id mapping of all items in the order
     */
    static itemIsReady(order: Order, item: Item, map: Map<string, Item> = null): boolean {
        if (!map) {
            map = this.itemMap(order);
        }

        return item.status === ItemStatus.ToDo &&
          item.dependencies.filter((id) => {
              const status = map.get(id).status;
              return  status !== ItemStatus.Complete;
          }).length === 0;
    }

    /**
   * Recursively gets all replaced items for a given item,
   * with most recent to least recent in the array
   *
   * @param order {Order} An order
   * @param item {Item} An item
   */
    static getReplacedItems(order: Order, item: Item): Item[] {
        return this.getReplacedItemsHelper(order, item, []);
    }
}
